// src/app/components/reporting/components/report-editor.tsx
//
// Editing a composed draft.
//
// The redesign's draft is a reading surface and the DC draws no editor. The
// product has one, it PUTs through `updateReport`, and removing the only way
// into it would strand a working endpoint — so it survives the restyle, moved
// out of the draft into its own mode and rebuilt on kit fields.
//
// What did NOT change: the payload, the endpoint, the five editable pieces
// (title, executive summary, sections, findings, recommendations), reordering,
// adding and deleting a section, and the toast on success or failure.
//
// What did: every hand-rolled input with a teal border and an inline style is
// now a kit field, the delete control is a kit IconButton with an accessible
// name it did not have, and the section number is derived from position rather
// than kept in the row.

import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button, FieldRow, IconButton, TextInput, Textarea } from "@/app/components/kit";
import { updateReport } from "@/app/services/reporting/insights-api-service";
import type { GeneratedReport, ReportSection } from "../types";

export interface ReportEditorProps {
  report: GeneratedReport;
  onSaved: (updated: GeneratedReport) => void;
  onCancel: () => void;
}

export function ReportEditor({ report, onSaved, onCancel }: ReportEditorProps) {
  const [title, setTitle] = useState(report.title);
  const [summary, setSummary] = useState(report.executive_summary);
  const [sections, setSections] = useState<ReportSection[]>(report.sections);
  const [findings, setFindings] = useState<string[]>(report.key_findings);
  const [recommendations, setRecommendations] = useState<string[]>(report.recommendations);
  const [saving, setSaving] = useState(false);

  const setSection = (i: number, next: ReportSection) =>
    setSections((p) => p.map((s, j) => (j === i ? next : s)));

  const move = (i: number, dir: -1 | 1) =>
    setSections((p) => {
      const to = i + dir;
      if (to < 0 || to >= p.length) return p;
      const a = [...p];
      [a[i], a[to]] = [a[to], a[i]];
      return a;
    });

  async function save() {
    setSaving(true);
    try {
      const updated = await updateReport(report.id, {
        title,
        audience: report.audience,
        format: report.format,
        confidence: report.confidence,
        executive_summary: summary,
        sections,
        key_findings: findings,
        recommendations,
        insight_count: report.insight_count,
      });
      onSaved(updated);
      toast.success("Report saved");
    } catch {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="upm-ir-editor" aria-label="Edit draft">
      <header className="upm-ir-editor__head">
        <h3 className="upm-ir-editor__title">Editing this draft</h3>
        <div className="upm-ir-editor__actions">
          <Button onClick={save} loading={saving} loadingLabel="Saving">
            Save changes
          </Button>
          <Button variant="secondary" icon={X} onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </header>

      <div className="upm-ir-editor__body">
        <FieldRow label="Report title">
          {({ id }) => (
            <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />
          )}
        </FieldRow>

        <FieldRow label="Executive summary">
          {({ id }) => (
            <Textarea id={id} value={summary} rows={4} onChange={(e) => setSummary(e.target.value)} />
          )}
        </FieldRow>

        {sections.map((s, i) => (
          <div className="upm-ir-editor__section" key={`section-${i}`}>
            <div className="upm-ir-editor__sectionhead">
              {/* Derived from position, so it cannot disagree with the order. */}
              <span className="upm-ir-editor__num">{String(i + 1).padStart(2, "0")}</span>
              <IconButton
                icon={ChevronUp}
                label={`Move section ${i + 1} up`}
                disabled={i === 0}
                onClick={() => move(i, -1)}
              />
              <IconButton
                icon={ChevronDown}
                label={`Move section ${i + 1} down`}
                disabled={i === sections.length - 1}
                onClick={() => move(i, 1)}
              />
              <span className="upm-ir-editor__spacer" />
              <IconButton
                icon={Trash2}
                label={`Delete section ${i + 1}`}
                onClick={() => setSections((p) => p.filter((_, j) => j !== i))}
              />
            </div>

            <FieldRow label="Section title">
              {({ id }) => (
                <TextInput
                  id={id}
                  value={s.title}
                  onChange={(e) => setSection(i, { ...s, title: e.target.value })}
                />
              )}
            </FieldRow>
            <FieldRow label="Key metric">
              {({ id }) => (
                <TextInput
                  id={id}
                  value={s.key_metric ?? ""}
                  onChange={(e) => setSection(i, { ...s, key_metric: e.target.value || null })}
                />
              )}
            </FieldRow>
            <FieldRow label="Content">
              {({ id }) => (
                <Textarea
                  id={id}
                  value={s.content}
                  rows={4}
                  onChange={(e) => setSection(i, { ...s, content: e.target.value })}
                />
              )}
            </FieldRow>
            <FieldRow label="Reasoning">
              {({ id }) => (
                <TextInput
                  id={id}
                  value={s.reasoning}
                  onChange={(e) => setSection(i, { ...s, reasoning: e.target.value })}
                />
              )}
            </FieldRow>
          </div>
        ))}

        <Button
          variant="secondary"
          icon={Plus}
          onClick={() =>
            setSections((p) => [
              ...p,
              { title: "", content: "", key_metric: null, source_insight_ids: [], reasoning: "" },
            ])
          }
        >
          Add section
        </Button>

        <ListEditor label="Key findings" items={findings} onChange={setFindings} />
        <ListEditor label="Recommendations" items={recommendations} onChange={setRecommendations} />
      </div>
    </section>
  );
}

function ListEditor({
  label,
  items,
  onChange,
}: {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="upm-ir-editor__list">
      <h4 className="upm-ir-columns__title">{label}</h4>
      {items.map((value, i) => (
        <div className="upm-ir-editor__listrow" key={`${label}-${i}`}>
          <TextInput
            value={value}
            aria-label={`${label} ${i + 1}`}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
          />
          <IconButton
            icon={Trash2}
            label={`Delete ${label.toLowerCase()} ${i + 1}`}
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <Button variant="ghost" icon={Plus} onClick={() => onChange([...items, ""])}>
        Add
      </Button>
    </div>
  );
}
